import { sortStrings } from './sortStrings';

type DateLike = string | number | Date;

export function sortByDateValue<K extends string>(
  key: K,
  order: 'asc' | 'desc' = 'desc',
) {
  if (order === 'desc') {
    return (a: Record<K, DateLike>, b: Record<K, DateLike>) =>
      new Date(b[key]).getTime() - new Date(a[key]).getTime();
  }
  return (a: Record<K, DateLike>, b: Record<K, DateLike>) =>
    new Date(a[key]).getTime() - new Date(b[key]).getTime();
}

export function sortByNumericValue<K extends string>(key: K) {
  return (a: Record<K, number>, b: Record<K, number>) => a[key] - b[key];
}

export function sortByStringValue<K extends string>(key: K) {
  return (a: Record<K, string>, b: Record<K, string>) =>
    sortStrings(a[key], b[key]);
}

export function getOptions<
  T extends Record<LK, string | null | undefined> & Record<VK, unknown>,
  VK extends string,
  LK extends string,
>(array: T[], valueKey: VK, labelKey: LK) {
  return array
    .map((entry) => ({
      label: entry[labelKey] || '',
      value: entry[valueKey],
    }))
    .sort(sortByStringValue('label'));
}

export function coerceToArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function getUniqueObjectsInArrayByKey<T, K extends keyof T>(
  array: T[],
  key: K,
) {
  return [...new Map(array.map((item) => [item[key], item] as const)).values()];
}

export function getItemValue<T>(array: Array<{ value: T }>) {
  return array.map((itm) => itm.value);
}

/**
 * Joins a list of strings into a string with expressive delimiter support
 *
 * @param list list of strings
 * @param options configuration options
 * @param options.delimiter default join delimiter
 * @param options.lastDelimiter the delimiter for the last item in the list
 */
export const joinWith = (
  list: string[],
  options: { delimiter?: string; lastDelimiter?: string | null } = {},
) => {
  const { delimiter = '', lastDelimiter = null } = options;
  const { length } = list;
  if (length === 1 || !lastDelimiter) {
    return list.join(delimiter);
  }

  return (
    list.slice(0, length - 1).join(delimiter) +
    `${lastDelimiter}${list[length - 1]}`
  );
};
